import { nanoid } from "nanoid";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PlusIcon from "../../assets/plusIconGreen.svg";
import { CoApplicantBox } from "./coApplicantBox";
import { convertToFormattedAmount } from "../../utils/convertAmount";
import { useCoApplicantStore } from "../../stores/coApplicantsStore";
import { useUserMortgageProfileStore } from "../../stores/userProfileStore";

const CoApplicantsDetailsView = () => {
  const navigate = useNavigate();
  const { userMortgageProfile } = useUserMortgageProfileStore();

  const {
    coApplicants,
    addCoApplicant,
    updateCoApplicantField,
    removeCoApplicant,
    clearCoApplicants,
  } = useCoApplicantStore();

  useEffect(() => {
    if (userMortgageProfile?.coApplicants) {
      clearCoApplicants();

      for (const coApplicant of userMortgageProfile.coApplicants) {
        const id = nanoid();
        addCoApplicant(id);

        updateCoApplicantField(id, "firstName", coApplicant.firstName || "");
        updateCoApplicantField(id, "middleName", coApplicant.middleName || "");
        updateCoApplicantField(id, "lastName", coApplicant.lastName || "");
        updateCoApplicantField(
          id,
          "address",
          coApplicant.address?.address1 || ""
        );
        updateCoApplicantField(
          id,
          "address2",
          coApplicant.address?.address2 || ""
        );
        updateCoApplicantField(id, "city", coApplicant.address?.city || "");
        updateCoApplicantField(
          id,
          "postalCode",
          coApplicant.address?.postalCode || ""
        );
        updateCoApplicantField(
          id,
          "province",
          coApplicant.address?.state || ""
        );
        updateCoApplicantField(id, "email", coApplicant.emailAddress || "");
        updateCoApplicantField(id, "phone", coApplicant.phoneNumber || "");
        updateCoApplicantField(
          id,
          "dateOfBirth",
          coApplicant.dateOfBirth || null
        );
        updateCoApplicantField(
          id,
          "maritalStatus",
          coApplicant.maritalStatus || ""
        );
        updateCoApplicantField(id, "relation", coApplicant.relationship || "");
        updateCoApplicantField(
          id,
          "primaryIncomeSource",
          coApplicant.incomeSource || ""
        );
        updateCoApplicantField(
          id,
          "isFirstTimeHomeBuyer",
          coApplicant.isFirstTimeHomeBuyer || false
        );
        // updateCoApplicantField(
        //   id,
        //   "totalMonthlyDebt",
        //   convertToFormattedAmount(coApplicant.totalMonthlyDebt.amount) || ""
        // );
        updateCoApplicantField(
          id,
          "annualBasePay",
          convertToFormattedAmount(coApplicant.annualBasePay.amount) || ""
        );
      }
    }
  }, [
    userMortgageProfile,
    addCoApplicant,
    updateCoApplicantField,
    clearCoApplicants,
  ]);
  return (
    <div className="space-y-4">
      <div>
        <div className="font-bold text-2xl">Co-Applicants</div>
        <div className="text-[#6B7280]">
          You indicated that you are applying with co-applicants. Please provide
          the information of the co-applicants. This goes over similar questions
          to the ones you received about information. Add up to 3 co-applicants,
          or skip to continue as a single applicant.
        </div>
      </div>
      <hr className="bg-[#6B7280]" />

      {coApplicants.map((coapplicant, index) => (
        <CoApplicantBox
          key={coapplicant.id}
          componentId={coapplicant.id}
          coapplicantNumber={index + 1}
          firstName={coapplicant.firstName ?? ""}
          middleName={coapplicant.middleName ?? ""}
          lastName={coapplicant.lastName ?? ""}
          address={coapplicant.address ?? ""}
          address2={coapplicant.address2 ?? ""}
          city={coapplicant.city ?? ""}
          postalCode={coapplicant.postalCode ?? ""}
          province={coapplicant.province ?? ""}
          email={coapplicant.email ?? ""}
          phone={coapplicant.phone ?? ""}
          dateOfBirth={coapplicant.dateOfBirth ?? null}
          maritalStatus={coapplicant.maritalStatus ?? ""}
          relation={coapplicant.relation ?? ""}
          primaryIncomeSource={coapplicant.primaryIncomeSource ?? ""}
          annualBasePay={coapplicant.annualBasePay ?? ""}
          isFirstTimeHomeBuyer={coapplicant.isFirstTimeHomeBuyer ?? null}
          // totalMonthlyDebt={coapplicant.totalMonthlyDebt ?? ""}
          firstNameError={coapplicant.errors?.firstName}
          lastNameError={coapplicant.errors?.lastName}
          addressError={coapplicant.errors?.address}
          cityError={coapplicant.errors?.city}
          postalCodeError={coapplicant.errors?.postalCode}
          provinceError={coapplicant.errors?.province}
          emailError={coapplicant.errors?.email}
          phoneError={coapplicant.errors?.phone}
          dateOfBirthError={coapplicant.errors?.dateOfBirth}
          maritalStatusError={coapplicant.errors?.maritalStatus}
          relationError={coapplicant.errors?.relation}
          primaryIncomeSourceError={coapplicant.errors?.primaryIncomeSource}
          annualBasePayError={coapplicant.errors?.annualBasePay}
          isFirstTimeHomeBuyerError={coapplicant.errors?.isFirstTimeHomeBuyer}
          // totalMonthlyDebtError={coapplicant.errors?.totalMonthlyDebt}
          setFirstName={(value) =>
            updateCoApplicantField(coapplicant.id, "firstName", value)
          }
          setMiddleName={(value) =>
            updateCoApplicantField(coapplicant.id, "middleName", value)
          }
          setLastName={(value) =>
            updateCoApplicantField(coapplicant.id, "lastName", value)
          }
          setAddress={(value) =>
            updateCoApplicantField(coapplicant.id, "address", value)
          }
          setAddress2={(value) =>
            updateCoApplicantField(coapplicant.id, "address2", value)
          }
          setCity={(value) =>
            updateCoApplicantField(coapplicant.id, "city", value)
          }
          setPostalCode={(value) =>
            updateCoApplicantField(coapplicant.id, "postalCode", value)
          }
          setProvince={(value) =>
            updateCoApplicantField(coapplicant.id, "province", value)
          }
          setEmail={(value) =>
            updateCoApplicantField(coapplicant.id, "email", value)
          }
          setPhone={(value) =>
            updateCoApplicantField(coapplicant.id, "phone", value)
          }
          setDateOfBirth={(value) =>
            updateCoApplicantField(coapplicant.id, "dateOfBirth", value)
          }
          setMaritalStatus={(value) =>
            updateCoApplicantField(coapplicant.id, "maritalStatus", value)
          }
          setRelation={(value) =>
            updateCoApplicantField(coapplicant.id, "relation", value)
          }
          setPrimaryIncomeSource={(value) =>
            updateCoApplicantField(coapplicant.id, "primaryIncomeSource", value)
          }
          setAnnualBasePay={(value) =>
            updateCoApplicantField(coapplicant.id, "annualBasePay", value)
          }
          setIsFirstTimeHomeBuyer={(value) =>
            updateCoApplicantField(
              coapplicant.id,
              "isFirstTimeHomeBuyer",
              value
            )
          }
          // setTotalMonthlyDebt={(value) =>
          //   updateCoApplicantField(coapplicant.id, "totalMonthlyDebt", value)
          // }
          onRemove={() => removeCoApplicant(coapplicant.id)}
        />
      ))}

      {coApplicants.length < 3 && (
        <button
          type="button"
          onClick={() => addCoApplicant()}
          className="w-full border border-[#E5E7EB] rounded-lg flex items-center p-4"
        >
          <img src={PlusIcon} alt="Add Co-Applicant" className="mr-2" />
          <span className="font-medium">
            Add Co-Applicant
          </span>
        </button>
      )}

      {coApplicants.length < 1 && (
        <button
          type="button"
          onClick={() => navigate("/down-payment")}
          className="w-full border border-[#E5E7EB] rounded-lg text-start flex items-center p-4"
        >
          <img src={PlusIcon} alt="Add Co-Applicant" className="mr-2" />
          <span className="font-medium">
            I am applying by myself for this mortgage
          </span>
        </button>
      )}
    </div>
  );
};

export { CoApplicantsDetailsView };
